import {
  CheckableAvatarSelectItem,
  CheckableSelectItem,
  CheckableTagSelectItem,
  Select,
  TagSelectSuggestion,
} from '@dixa/lib-shared-habla';
import { convertChannelCasing, PaginatedApiArg } from 'lib-shared-api-utils';
import { Tag } from 'lib-shared-legacy-types';
import { PaginatedApiResponse } from 'lib-shared-types-api';
import type {
  SearchFilterField,
  SearchQueueHit,
  SearchTagHit,
  SearchUserHit,
} from 'lib-shared-types-api';
import { Channel } from 'lib-shared-types-channels';
import type { LodashMap1x1 } from 'lodash/fp';
import map from 'lodash/fp/map';
import {
  ComponentType,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { searchApi } from 'apis/search';
import {
  useLazyPaginatedEndpoint,
  UseLazyPaginatedOptions,
  UsePaginatedResult,
} from 'utils/apis/pagination/useLazyPaginatedEndpoint';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import {
  filterItems,
  FilterItemType,
} from '../../../../../../utils/definitions';
import { ConditionFieldProps } from '../../conditionField';

import { useConditionFieldSelect } from './utils/useConditionFieldSelect';

type SearchHit = SearchQueueHit | SearchUserHit | SearchTagHit;

type SearchItemFormatter<TSearchHit extends SearchHit> = LodashMap1x1<
  TSearchHit,
  FilterItemType
>;

const defaultItemFormatter = map<SearchQueueHit, FilterItemType>(
  ({ id, name }) => ({ id, name }),
);

const tagItemFormatter = map<SearchTagHit, FilterItemType>(
  ({ id, name, color }) => ({
    id,
    name,
    color,
  }),
);

const userItemFormatter = map<SearchUserHit, FilterItemType>(
  ({ id, display_name, phone_number, email }) => ({
    id,
    name: display_name || email || phone_number || id,
  }),
);

function createFreeTextFilter(
  field: SearchFilterField,
): FunctionComponent<ConditionFieldProps> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const [search, setSearch] = useState('');
    const selectedItems = mapValuesToItems();
    const { disabled } = props;

    const emptyMessages: Partial<Record<SearchFilterField, string>> = {
      numeric_id: 'Enter the numeric conversation ID (e.g. 123456)',
    };

    // When there is a search input, don't render the empty state.
    const emptyComponent = search ? () => null : undefined;

    /**
     * Handles adding a new ID to the selection. If the ID starts with #, we
     * strip ID. If the ID contains any other characters than digits, we don't
     * add it.
     */
    const handleAddId = (item: FilterItemType) => {
      const id = item.id.toString().replace('#', '');
      const isValid = /^[0-9]*$/.test(id);

      if (isValid) {
        handleChange({ id, name: `#${id}` });
      }
      setSearch('');
    };

    return (
      <Select<FilterItemType>
        multiple
        clearable
        searchable
        placeholder="Type a value and click 'Add'"
        items={[]}
        selectedItems={selectedItems}
        onChange={handleAddId}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={disabled}
        onSearch={setSearch}
        suggestionComponent={TagSelectSuggestion}
        onSuggest={(id: string) => handleAddId({ id, name: `#${id}` })}
        renderSuggestion={(id: string) => `Add "#${id.replace('#', '')}"`}
        emptyMessage={emptyMessages[field]}
        emptyComponent={emptyComponent}
      />
    );
  }

  return Filter;
}

function createStaticFilter(
  field: SearchFilterField,
): FunctionComponent<ConditionFieldProps> {
  function Filter({
    condition,
    onChange,
    ...props
  }: ConditionFieldProps): JSX.Element {
    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const flags = useFeatureFlag(['instagram-management']);
    const channelFlags = getChannelFeatureFlagsStatus();

    const items = useMemo(() => {
      return field === 'initial_channel'
        ? filterItems[field].filter(({ id: channel }) => {
            const casedChannel = convertChannelCasing(
              channel as string,
              'snake',
            ) as Channel;
            if (casedChannel === 'facebook_messenger') {
              // Instagram conversations appear as Facebook conversations
              return (
                channelFlags.get(casedChannel) || flags.instagramManagement
              );
            }
            return channelFlags.get(casedChannel);
          })
        : filterItems[field];
    }, [flags, channelFlags]);

    const selectedItems = mapValuesToItems();

    const { placeholder, disabled } = props;

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        items={items}
        selectedItems={selectedItems}
        onChange={handleChange}
        onClear={handleClear}
        hideSelectedItems
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

type CreateDynamicFilterConfig<
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends SearchHit,
> = {
  readonly endpoint: {
    readonly options: UseLazyPaginatedOptions<
      TPaginatedApiArg,
      TPaginatedApiResponse,
      TSearchHit
    >;
    readonly defaultArg: Parameters<
      UsePaginatedResult<TSearchHit, TPaginatedApiArg>['changeArg']
    >[0];
  };
  readonly itemComponent: ComponentType<typeof CheckableSelectItem>;
  readonly itemFormatter: SearchItemFormatter<TSearchHit>;
  readonly prepareDisabled?: (
    selectedItems: FilterItemType[],
    items: FilterItemType[],
  ) => FilterItemType['id'][];
};

function createDynamicFilter<
  TPaginatedApiArg extends PaginatedApiArg,
  TPaginatedApiResponse extends PaginatedApiResponse,
  TSearchHit extends SearchHit,
>({
  endpoint: { options, defaultArg },
  itemComponent = CheckableSelectItem,
  itemFormatter,
  prepareDisabled,
}: CreateDynamicFilterConfig<
  TPaginatedApiArg,
  TPaginatedApiResponse,
  TSearchHit
>): FunctionComponent<ConditionFieldProps> {
  function Filter({
    condition,
    placeholder,
    disabled,
    onChange,
  }: ConditionFieldProps) {
    const [searchTerm, setSearchTerm] = useState<string>();

    const {
      items: hits,
      arg,
      asyncStatus,
      changeArg,
      hasNextPage,
      nextPage,
    } = useLazyPaginatedEndpoint({ ...options, infinite: true });

    useEffect(() => {
      if (searchTerm !== arg?.searchTerm) {
        changeArg(
          {
            ...defaultArg,
            searchTerm,
            offset: undefined,
            limit: undefined,
          },
          {
            debounceTime: 300,
          },
        );
      }
      // Don't rely on arg as dependency because we change the arg inside.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changeArg, searchTerm]);

    const { handleChange, handleClear, mapValuesToItems } =
      useConditionFieldSelect({ condition, onChange });

    const handleSearch = useCallback((value?: string) => {
      setSearchTerm(value?.trim());
    }, []);

    const handleToggle = useCallback(
      (isOpen: boolean) => {
        if (isOpen && !hits.length) {
          changeArg(
            {
              ...defaultArg,
              searchTerm,
              offset: undefined,
              limit: undefined,
            },
            { debounceTime: 0 },
          );
        } else if (!isOpen && searchTerm !== '') {
          setSearchTerm('');
        }
      },
      [changeArg, hits.length, searchTerm],
    );

    const items = itemFormatter(hits);
    const selectedItems = useMemo(() => mapValuesToItems(), [mapValuesToItems]);

    return (
      <Select
        multiple
        clearable
        searchable
        placeholder={placeholder}
        onSearch={handleSearch}
        onChange={handleChange}
        onClear={handleClear}
        onToggle={handleToggle}
        pagination={{
          infinite: true,
          nextEnable: hasNextPage,
          previousEnable: false,
          onNext: () => Promise.resolve(nextPage()),
        }}
        items={items}
        selectedItems={selectedItems}
        disabledItems={prepareDisabled?.(selectedItems, items)}
        itemComponent={itemComponent}
        loading={asyncStatus === 'pending'}
        limit={3}
        disabled={disabled}
      />
    );
  }

  return Filter;
}

export const ConditionFieldId = createFreeTextFilter('numeric_id');
export const ConditionFieldDirection = createStaticFilter('initial_direction');
export const ConditionFieldStatus = createStaticFilter('status');
export const ConditionFieldChannel = createStaticFilter('initial_channel');

export const ConditionFieldQueue = createDynamicFilter({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchQueue,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'name', direction: 'asc' },
    },
  },
  itemComponent: CheckableSelectItem,
  itemFormatter: defaultItemFormatter,
});

export const ConditionFieldContacts = createDynamicFilter({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchUser,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'display_name', direction: 'asc' },
      filter: {
        facets: {
          roles: ['enduser'],
        },
      },
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldAgents = createDynamicFilter({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchUser,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'display_name', direction: 'asc' },
      filter: {
        facets: {
          roles: ['admin', 'systemadmin', 'teamlead', 'agent'],
        },
      },
    },
  },
  itemComponent: CheckableAvatarSelectItem,
  itemFormatter: userItemFormatter,
});

export const ConditionFieldTags = createDynamicFilter({
  endpoint: {
    options: {
      endpoint: searchApi.endpoints.readSearchTag,
      itemsSelector: ({ data: { results } }) => results.hits ?? [],
    },
    defaultArg: {
      sort: { key: 'name', direction: 'asc' },
    },
  },
  itemComponent: CheckableTagSelectItem,
  itemFormatter: tagItemFormatter,
  prepareDisabled: (_, tags) =>
    tags.filter((tag) => (tag as Tag).is_deactivated).map((tag) => tag.id),
});
